/**
 * features/credit/CreditNotesManager.tsx
 */
import { useState } from 'react';
import { FileMinus, Plus, Download, Mail } from 'lucide-react';
import { useSettings } from '@/contexts/SettingsContext';
import { useCreditNotes } from './useCreditNotes';
import { CreditNoteEditor } from './CreditNoteEditor';
import { downloadCreditNotePdf, creditNotePdfBase64, creditNoteFileName } from '@/lib/creditNotePdf';
import { formatPula } from '@/lib/money';
import { SendDocumentModal } from '@/features/common/SendDocumentModal';
import type { CreditNote } from '@/types';
import { Button, Card, Badge, Spinner } from '@/components/ui/primitives';

function fmtDate(iso: string) {
  return iso ? new Date(iso).toLocaleDateString('en-BW', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
}

export function CreditNotesManager() {
  const { settings } = useSettings();
  const { creditNotes, loading } = useCreditNotes();
  const [editor, setEditor] = useState<CreditNote | null | 'new'>(null);
  const [emailing, setEmailing] = useState<CreditNote | null>(null);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-end">
        <Button icon={<Plus size={16} />} onClick={() => setEditor('new')}>
          New credit note
        </Button>
      </div>

      {loading ? (
        <Spinner label="Loading credit notes…" />
      ) : creditNotes.length === 0 ? (
        <Card className="py-16 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-brandSoft text-brand">
            <FileMinus size={22} />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400">No credit notes yet. Create one to reverse or correct an issued invoice.</p>
        </Card>
      ) : (
        <Card className="overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-black/5 text-left text-xs uppercase tracking-wide text-primaryLight dark:border-white/10 dark:text-gray-400">
                <th className="px-4 py-3 font-semibold">Number</th>
                <th className="px-4 py-3 font-semibold">Client</th>
                <th className="px-4 py-3 font-semibold">Against</th>
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 text-right font-semibold">Total</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {creditNotes.map((c) => (
                <tr key={c.id} className="border-b border-black/5 transition last:border-0 hover:bg-brandSoft/40 dark:border-white/5 dark:hover:bg-white/5">
                  <td className="cursor-pointer px-4 py-3 font-semibold text-primary dark:text-gray-100" onClick={() => setEditor(c)}>
                    {c.number ?? 'Draft'}
                  </td>
                  <td className="px-4 py-3 text-primary dark:text-gray-200">{c.client.name}</td>
                  <td className="px-4 py-3 text-primaryLight dark:text-gray-400">{c.invoiceNumber}</td>
                  <td className="px-4 py-3 text-primaryLight dark:text-gray-400">{fmtDate(c.issueDate)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-primary dark:text-gray-100">{formatPula(c.totals.total)}</td>
                  <td className="px-4 py-3">
                    <Badge tone={c.status === 'issued' ? 'success' : c.status === 'void' ? 'danger' : 'neutral'}>{c.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {c.status !== 'draft' && settings && (
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => downloadCreditNotePdf(c, settings)} title="Download" className="rounded-lg p-1.5 text-primaryLight hover:bg-brandSoft hover:text-brand dark:text-gray-300 dark:hover:bg-white/5">
                          <Download size={16} />
                        </button>
                        <button onClick={() => setEmailing(c)} title="Email" className="rounded-lg p-1.5 text-primaryLight hover:bg-brandSoft hover:text-brand dark:text-gray-300 dark:hover:bg-white/5">
                          <Mail size={16} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {editor !== null && <CreditNoteEditor creditNote={editor === 'new' ? null : editor} onClose={() => setEditor(null)} />}

      {emailing && settings && (
        <SendDocumentModal
          heading={`Email credit note ${emailing.number}`}
          to={emailing.client.email ?? ''}
          subject={`Credit note ${emailing.number} — Acepsle`}
          message={`Dear ${emailing.client.name},\n\nPlease find attached credit note ${emailing.number} against invoice ${emailing.invoiceNumber}.\n\nKind regards,\nAcepsle Proprietary Limited`}
          filename={creditNoteFileName(emailing)}
          contentBase64={creditNotePdfBase64(emailing, settings)}
          relatedEntityType="creditNote"
          relatedEntityId={emailing.id}
          onClose={() => setEmailing(null)}
        />
      )}
    </div>
  );
}
